/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import type { MedicalInfo, ProtectedProfile } from "@/types/safecircle";

export interface EmergencySmsInput {
  lat: number;
  lng: number;
  /** Short ID to reference this event in follow-up comms (e.g. last 6 of eventId). */
  eventShortCode?: string;
  profile?: Pick<ProtectedProfile, "name" | "medicalInfo"> | null;
  includeMedical?: boolean;
}

const MAX_SMS_LEN = 160;

/**
 * Brand prefix on every outbound SMS so the body matches the A2P 10DLC
 * campaign sample messages carriers byte-compare against. Literal
 * "MyAmbits: " — capital M, capital A, colon, one trailing space.
 */
const BRAND_PREFIX = "MyAmbits: ";

/**
 * Build a short, SMS-safe live-SOS body. The core line is plain-language and
 * matches the registered A2P 10DLC campaign sample EXACTLY:
 *
 *   MyAmbits: [Name] triggered an SOS and may need help. Last location: [link]
 *
 * The maps link carries the coordinates (no raw inline coords). The internal
 * event short-code is NOT rendered to the recipient (it's display-only,
 * derived from eventId, and used in-app — see SOSActiveScreen/SOSReceiverScreen).
 *
 * `includeMedical` appends a compact BT:/Alg:/Rx: summary. This is used ONLY by
 * the in-app "Share with 911" draft (user's own Messages app → 911), where the
 * extra clinical detail helps responders and A2P sample-matching does not apply.
 * The A2P-registered emergency-contact send (sendSosSms) passes
 * includeMedical:false so its body matches the sample verbatim and never
 * exceeds a single GSM-7 segment.
 */
export function buildEmergencySms(input: EmergencySmsInput): string {
  const mapsUrl = `https://maps.google.com/?q=${input.lat.toFixed(5)},${input.lng.toFixed(5)}`;
  // Graceful fallback so an empty name never yields a double space.
  const name = input.profile?.name?.trim() || "Someone";
  const head = `${BRAND_PREFIX}${name} triggered an SOS and may need help. Last location: ${mapsUrl}`;

  if (!input.includeMedical || !input.profile?.medicalInfo) {
    return truncate(head, MAX_SMS_LEN);
  }

  const med = summarizeMedical(input.profile.medicalInfo);
  if (!med) return truncate(head, MAX_SMS_LEN);

  const full = `${head} ${med}`;
  return truncate(full, MAX_SMS_LEN);
}

export function summarizeMedical(m: MedicalInfo): string {
  const parts: string[] = [];
  if (m.bloodType) parts.push(`BT:${m.bloodType}`);
  if (m.allergies?.length) parts.push(`Alg:${m.allergies.slice(0, 3).join("/")}`);
  if (m.medications?.length) parts.push(`Rx:${m.medications.slice(0, 2).join("/")}`);
  return parts.join(" ");
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}

/**
 * Formats the full sms: URL with body pre-populated. The special `911` target
 * is treated like any other number; most mobile OSes open the SMS app with
 * the body drafted.
 */
export function buildEmergencySmsHref(
  recipient: string,
  body: string,
): string {
  // iOS uses `&body=`, Android uses `?body=`. Using `?body=` with a leading
  // separator works on both in practice.
  return `sms:${recipient}?&body=${encodeURIComponent(body)}`;
}
